import { useCallback, useRef, useSyncExternalStore } from 'react';

import ResizeObserver from 'resize-observer-polyfill';

/**
 * Marks the editor's horizontal scroll container in `WordProcessor`. The document keeps a
 * minimum width, so it overflows — and scrolls — inside that container whenever the right
 * panel takes enough of the column.
 */
export const EDITOR_COLUMN_SELECTOR = '[data-editor-scroll-x]';

export interface EditorColumnBounds {
  /** How far the document is scrolled horizontally inside the column. */
  scrollLeftPx: number;
  /** The width of the column that is on screen, i.e. what the right panel left behind. */
  visibleWidthPx: number;
}

const UNMEASURED_BOUNDS: EditorColumnBounds = { scrollLeftPx: 0, visibleWidthPx: 0 };

/**
 * Report the visible horizontal bounds of the editor column that `descendant` renders in.
 * They are read from the DOM during render via `useSyncExternalStore`, so anything sized
 * from them is correct on its first paint rather than a frame later.
 */
export function useEditorColumnBounds(descendant: HTMLElement | null): EditorColumnBounds {
  const snapshotRef = useRef(UNMEASURED_BOUNDS);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const column = descendant?.closest(EDITOR_COLUMN_SELECTOR);
      if (!column) return () => {};

      column.addEventListener('scroll', onStoreChange, { passive: true });

      /**
       * Resizing the right panel resizes the column. Notifying is deferred to the next frame,
       * and coalesced, because browsers report a same-cycle chain of resizes as "ResizeObserver
       * loop limit exceeded" and Cypress fails the run on it.
       */
      let resizeFrame: number | null = null;
      const resizeObserver = new ResizeObserver(() => {
        if (resizeFrame !== null) return;
        resizeFrame = window.requestAnimationFrame(() => {
          resizeFrame = null;
          onStoreChange();
        });
      });
      resizeObserver.observe(column);

      return () => {
        column.removeEventListener('scroll', onStoreChange);
        resizeObserver.disconnect();
        if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
      };
    },
    [descendant]
  );

  const getSnapshot = useCallback(() => {
    const column = descendant?.closest(EDITOR_COLUMN_SELECTOR);
    if (!column) return UNMEASURED_BOUNDS;

    const bounds = { scrollLeftPx: column.scrollLeft, visibleWidthPx: column.clientWidth };

    // Snapshots are compared by identity, so keep returning the previous one until a value changes.
    const previous = snapshotRef.current;
    if (
      previous.scrollLeftPx === bounds.scrollLeftPx &&
      previous.visibleWidthPx === bounds.visibleWidthPx
    )
      return previous;

    snapshotRef.current = bounds;
    return bounds;
  }, [descendant]);

  return useSyncExternalStore(subscribe, getSnapshot, () => UNMEASURED_BOUNDS);
}
